import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Save, CheckCircle, Wrench, AlertCircle, MessageSquare } from 'lucide-react';
import Layout from './Layout';
import { useStore } from '../context/StoreContext';
import { useAlert } from '../context/AlertContext';
import './Management.css';

const Facility = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const queryDate = queryParams.get('date');

    const { getStore, updateStore, user, currentDate } = useStore();
    const { showAlert } = useAlert();
    const store = getStore(id);
    const today = currentDate;
    const targetDate = queryDate || today;

    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 60000);
        return () => clearInterval(timer);
    }, []);

    const isPastCutoff = currentTime.getHours() === 23 ? currentTime.getMinutes() >= 50 : currentTime.getHours() > 23;
    const isMyStore = user?.role === 'admin' || String(user?.storeId) === String(id);

    // 월간 마감 잠금 체크 - 사용자 요청으로 비활성화
    // const todayObj = new Date(today);
    // const targetObj = new Date(targetDate);
    // const isMonthLocked = (targetObj.getFullYear() < todayObj.getFullYear()) ||
    //     (targetObj.getFullYear() === todayObj.getFullYear() && targetObj.getMonth() < todayObj.getMonth());
    const isMonthLocked = false;

    const canEdit = !isMonthLocked && ((user?.role === 'admin') || (targetDate === today && isMyStore && !isPastCutoff));
    const isReadOnlyWarning = !isMonthLocked && targetDate === today && isMyStore && isPastCutoff && user?.role !== 'admin';

    const [items, setItems] = useState([]);
    const [notes, setNotes] = useState(''); // Add notes state
    const [saveMessage, setSaveMessage] = useState(false);
    const [currentStoreName, setCurrentStoreName] = useState('');

    // 보안 권한 체크
    useEffect(() => {
        if (!user) {
            navigate('/login');
            return;
        }
        if (user.role !== 'admin' && String(user.storeId) !== String(id)) {
            showAlert('자신이 담당하는 매장의 시설 현황만 관리할 수 있습니다.');
            navigate('/dashboard');
        }
    }, [user, navigate, id]);

    useEffect(() => {
        if (!store) return;

        setCurrentStoreName(store.name);

        // 날짜별 히스토리 우선 조회
        const history = store.facilityHistory?.[targetDate];
        const historyItems = history?.items || [];
        const masterItems = store.facilityData?.items || [];

        // ⚠️ 과거 데이터 보호 로직:
        // - 오달 날짜이면: 현재 설정(masterItems) 기준으로 표시 (= 힉재는 설정대로)
        // - 과거 날짜이면: 히스토리에 저장된 이름 우선 (저장 당시의 이름 보존)
        let initialData;
        const pastDates = Object.keys(store.facilityHistory || {}).sort((a, b) => b.localeCompare(a)).filter(d => d < targetDate);
        const lastRecord = pastDates.length > 0 ? store.facilityHistory[pastDates[0]] : null;
        const lastItems = lastRecord?.items || [];

        if (targetDate === today) {
            // 오늘 날짜인 경우 최신 설정(masterItems)의 이름을 최우선으로 적용하고,
            // 상태/비고만 히스토리(historyItems 또는 lastItems)에서 가져옵니다.
            if (masterItems.length > 0) {
                initialData = masterItems.map((mItem, idx) => {
                    const sourceItem = historyItems[idx] || lastItems[idx];
                    return {
                        id: idx + 1,
                        name: mItem.name, // 항상 최신 설정 이름 사용
                        status: sourceItem ? sourceItem.status : 'normal',
                        note: sourceItem ? sourceItem.note : ''
                    };
                });
            } else {
                initialData = [
                    { id: 1, name: 'POS/결제기기', status: 'normal', note: '' },
                    { id: 2, name: '주방설비 (튀김기/그릴)', status: 'normal', note: '' },
                    { id: 3, name: '냉장/냉동고', status: 'normal', note: '' },
                    { id: 4, name: '조명/전기시설', status: 'normal', note: '' },
                ];
            }
        } else if (historyItems.length > 0) {
            // 과거 날짜: 기존 히스토리 데이터 보존
            initialData = historyItems.map((hItem, idx) => ({
                id: idx + 1,
                name: hItem.name || `항목 ${idx + 1}`,
                status: hItem.status || 'normal',
                note: hItem.note || ''
            }));
        } else {
            // 과거 날짜에 기록이 없는 경우
            initialData = masterItems.length > 0 ? masterItems.map((mItem, idx) => ({
                id: idx + 1,
                name: mItem.name,
                status: 'normal',
                note: ''
            })) : [
                { id: 1, name: 'POS/결제기기', status: 'normal', note: '' },
                { id: 2, name: '주방설비 (튀김기/그릴)', status: 'normal', note: '' },
                { id: 3, name: '냉장/냉동고', status: 'normal', note: '' },
                { id: 4, name: '조명/전기시설', status: 'normal', note: '' },
            ];
        }

        setItems(prev => {
            if (JSON.stringify(prev) !== JSON.stringify(initialData)) return initialData;
            return prev;
        });

        // 비고 내용 로드 (버그 수정)
        if (history?.notes) {
            setNotes(history.notes);
        } else {
            setNotes('');
        }
    }, [id, targetDate, store]);

    const handleStatusChange = (itemId, newStatus) => {
        setItems(prev => prev.map(item =>
            item.id === itemId ? { ...item, status: newStatus } : item
        ));
    };

    const handleNameChange = (itemId, newName) => {
        setItems(prev => prev.map(item =>
            item.id === itemId ? { ...item, name: newName } : item
        ));
    };

    const handleNoteChange = (itemId, newNote) => {
        setItems(prev => prev.map(item =>
            item.id === itemId ? { ...item, note: newNote } : item
        ));
    };

    const handleSave = () => {
        if (!canEdit) {
            showAlert('보고 마감 시간 이후 또는 과거 데이터는 수정할 수 없습니다.');
            return;
        }

        const store = getStore(id);
        const facilityHistory = store?.facilityHistory || {};

        // 검증 로직: 수정 시 또는 관리자(Master 포함) 최초 입력 시 비고 필수
        const isEditing = !!facilityHistory[targetDate];
        const currentUserId = (user?.id || '').toLowerCase();
        const currentUserRole = (user?.role || '').toLowerCase();
        const isAdminAccount = currentUserRole === 'admin' || currentUserId === 'master' || currentUserId.startsWith('admin');
        
        if (isEditing || isAdminAccount) {
            if (!notes || notes.trim() === '') {
                const alertMsg = isEditing 
                    ? '💡 보다 정확한 데이터 관리를 위해 하단 비고란에 구체적인 수정 사유를 입력해 주세요.\n(예: 오타 수정, 설비 상태 재확인 등)'
                    : '📢 관리자 대면/대리 입력 시에는 반드시 하단 비고란에 해당 사유를 입력해 주시기 바랍니다.\n(예: 매니저 요청 대리 입력, 현장 수리 확인 후 입력 등)';
                showAlert(alertMsg);
                return;
            }
            if (isEditing && notes.trim() === (facilityHistory[targetDate].notes || '').trim()) {
                showAlert('✍️ 이전 기록과 사유가 동일한 내용입니다. 변경된 부분에 대한 구체적인 사유를 추가로 작성해 주세요.\n(예: 사유 보충, 변경 사항 상세 기술 등)');
                return;
            }
        }

        const dataToSave = items.map(item => ({
            name: item.name,
            status: item.status,
            note: item.note
        }));

        const dailyStatus = store?.dailyStatus || {};
        const currentDayStatus = dailyStatus[targetDate] || {};

        updateStore(id, {
            facilityData: { items: dataToSave }, // 실시간 데이터 업데이트
            dailyStatus: {
                ...dailyStatus,
                [targetDate]: {
                    ...currentDayStatus,
                    facility: true,
                    notes: notes // Add global notes to status or history
                }
            },
            facilityHistory: {
                ...facilityHistory,
                [targetDate]: {
                    items: dataToSave,
                    notes: notes, // Store global notes in history too
                    isEdited: !!facilityHistory[targetDate] || facilityHistory[targetDate]?.isEdited,
                    lastModified: !!facilityHistory[targetDate] ? new Date().toISOString() : null
                }
            }
        });

        setSaveMessage(true);
        setTimeout(() => {
            setSaveMessage(false);
            // 발주 관리로 이동
            navigate(`/store/${id}/order`);
        }, 2000);
    };

    const getStatusConfig = (status) => {
        switch (status) {
            case 'normal': return { label: '정상', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)', icon: <CheckCircle size={14} /> };
            case 'warning': return { label: '점검필요', color: '#eab308', bg: 'rgba(234, 179, 8, 0.1)' };
            case 'malfunction': return { label: '작동불량', color: '#f97316', bg: 'rgba(249, 115, 22, 0.1)' };
            case 'broken': return { label: '고장/수리중', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' };
            default: return { label: '미정', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.1)' };
        }
    };

    const formattedDate = targetDate.replace(/-/g, '. ');

    return (
        <Layout>
            <div className="management-content">
                <div className="management-header-flex">
                    <h1>시설 관리 <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>v2.5</span> <span>- {store?.name || ''}</span></h1>
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div className="mobile-store-name">{currentStoreName}</div>
                        <div className="date-display-badge" style={{ padding: '6px 16px', fontSize: '0.9rem' }}>
                            <Wrench size={16} />
                            <span>{formattedDate}</span>
                            {store?.facilityHistory?.[targetDate]?.isEdited && (
                                <span style={{
                                    fontSize: '0.75rem',
                                    padding: '2px 8px',
                                    background: 'rgba(234, 179, 8, 0.25)',
                                    color: '#facc15',
                                    borderRadius: '6px',
                                    fontWeight: '800',
                                    marginLeft: '10px',
                                    border: '1px solid rgba(234, 179, 8, 0.3)'
                                }}>
                                    수정됨
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {saveMessage && (
                    <div className="success-overlay">
                        <CheckCircle size={24} />
                        <span>시설 상태가 성공적으로 저장되었습니다!</span>
                    </div>
                )}

                {isMonthLocked && (
                    <div className="admin-only-alert" style={{ marginBottom: '20px', backgroundColor: 'rgba(148, 163, 184, 0.15)', borderColor: '#94a3b8', color: '#94a3b8' }}>
                        <Save size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                        해당 월의 데이터는 월간 마감이 완료되어 수정할 수 없는 보관 상태입니다.
                    </div>
                )}

                {/* 최근 3일 추이 요약 (컴팩트 버전) */}
                <div style={{
                    display: 'flex',
                    gap: '6px',
                    marginBottom: '16px',
                    width: '100%'
                }}>
                    {[1, 2, 3].map(daysAgo => {
                        const d = new Date(targetDate);
                        d.setDate(d.getDate() - daysAgo);
                        const dateStr = d.getFullYear() + '-' +
                            String(d.getMonth() + 1).padStart(2, '0') + '-' +
                            String(d.getDate()).padStart(2, '0');

                        const history = store?.facilityHistory?.[dateStr];
                        const issues = history?.items?.filter(f => f.status && f.status !== 'normal').length || 0;

                        return (
                            <div key={dateStr} className="glass-card" style={{
                                flex: 1,
                                padding: '6px 4px',
                                textAlign: 'center',
                                opacity: 0.85,
                                minWidth: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                            }}>
                                <div style={{ fontSize: '0.6rem', color: '#94a3b8', marginBottom: '1px' }}>
                                    {dateStr.slice(5).replace('-', '/')}
                                </div>
                                <div style={{
                                    fontSize: '0.75rem',
                                    fontWeight: '800',
                                    color: issues > 0 ? '#f87171' : (history ? '#4ade80' : '#94a3b8'),
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {history ? (issues > 0 ? `이상 ${issues}` : '정상') : '미기록'}
                                </div>
                            </div>
                        );
                    })}
                </div>


                {isReadOnlyWarning && (
                    <div className="admin-only-alert" style={{ marginBottom: '20px', backgroundColor: 'rgba(249, 115, 22, 0.15)', borderColor: '#f97316', color: '#f97316' }}>
                        <AlertCircle size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                        금일 점검 보고는 23:50에 마감되었습니다. 이후 수정은 관리자에게 문의하세요.
                    </div>
                )}

                <div className="card-header" style={{ marginBottom: '10px' }}>
                    <div className="status-indicator-group">
                        {store?.dailyStatus?.[targetDate]?.facility ? (
                            <div className="submit-badge" style={{ background: 'rgba(168, 85, 247, 0.2)', color: '#a855f7', borderColor: 'rgba(168, 85, 247, 0.3)' }}>
                                <CheckCircle size={16} /> 시설 점검 완료
                            </div>
                        ) : (
                            <div className="submit-badge pending">
                                시설 점검 대기
                            </div>
                        )}
                        {/* 저장 버튼: 권한이 있는 경우에만 표시 */}
                        <div className="facility-actions">
                            <button className="save-button" onClick={handleSave}>
                                <Save size={18} />
                                수정한 내용 저장하기
                            </button>
                        </div>
                    </div>


                </div>

                <div className="glass-card management-card" style={{ width: '100%' }}>
                    <div className="inventory-info-header" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
                        <AlertCircle size={20} color="#38bdf8" />
                        <p style={{ margin: 0, color: '#cbd5e1' }}>매장의 주요 기기 및 시설 상태를 체크하고 특이사항을 기록해 주세요.</p>
                    </div>

                    {/* Mobile Summary Row (visible on mobile only) */}
                    <div className="mobile-only-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, 1fr)',
                        gap: '8px',
                        marginBottom: '20px'
                    }}>
                        <div className="glass-card" style={{ padding: '10px', textAlign: 'center' }}>
                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>전체 항목</div>
                            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: '#f8fafc' }}>{items.length}</div>
                        </div>
                        <div className="glass-card" style={{ padding: '10px', textAlign: 'center' }}>
                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>이상 항목</div>
                            <div style={{ fontSize: '1.1rem', fontWeight: '700', color: items.filter(f => f.status !== 'normal').length > 0 ? '#f87171' : '#4ade80' }}>
                                {items.filter(f => f.status !== 'normal').length}
                            </div>
                        </div>
                    </div>

                    {/* Compact Grid Layout for Facility Items */}
                    <div className="facility-list" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '12px',
                        width: '100%'
                    }}>
                        {items.map(item => {
                            const config = getStatusConfig(item.status);
                            return (
                                <div key={item.id} className="facility-item-card glass-card" style={{
                                    padding: '10px 12px',
                                    marginBottom: '0', // Grid handles gap
                                    boxSizing: 'border-box',
                                    width: '100%',
                                    border: item.status !== 'normal' ? `1px solid ${config.color}66` : '1px solid rgba(255, 255, 255, 0.08)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    transition: 'all 0.2s ease'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'nowrap', gap: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                                            <div style={{
                                                width: '28px',
                                                height: '28px',
                                                borderRadius: '6px',
                                                background: config.bg,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: config.color,
                                                flexShrink: 0
                                            }}>
                                                <Wrench size={14} />
                                            </div>
                                            <div style={{ minWidth: 0 }}>
                                                <h4 style={{ 
                                                    margin: 0, 
                                                    fontSize: '0.9rem', 
                                                    color: '#f8fafc', 
                                                    whiteSpace: 'nowrap', 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis',
                                                    lineHeight: '1.2'
                                                }}>
                                                    {item.name}
                                                </h4>
                                                <span style={{ fontSize: '0.65rem', color: config.color, fontWeight: '700' }}>{config.label}</span>
                                            </div>
                                        </div>

                                        <div className="status-selector" style={{ 
                                            display: 'grid', 
                                            gridTemplateColumns: 'repeat(2, 1fr)', 
                                            gap: '3px', 
                                            width: 'auto',
                                            flexShrink: 0
                                        }}>
                                            {['normal', 'warning', 'malfunction', 'broken'].map(status => (
                                                <button
                                                    key={status}
                                                    type="button"
                                                    onClick={() => canEdit && handleStatusChange(item.id, status)}
                                                    className={`status-btn ${item.status === status ? 'active' : ''}`}
                                                    style={{
                                                        padding: '4px 6px',
                                                        borderRadius: '4px',
                                                        fontSize: '0.65rem',
                                                        fontWeight: '700',
                                                        border: '1px solid',
                                                        backgroundColor: item.status === status ? getStatusConfig(status).bg : 'rgba(255, 255, 255, 0.02)',
                                                        color: item.status === status ? getStatusConfig(status).color : '#94a3b8',
                                                        borderColor: item.status === status ? getStatusConfig(status).color : 'rgba(255, 255, 255, 0.05)',
                                                        cursor: canEdit ? 'pointer' : 'default',
                                                        whiteSpace: 'nowrap',
                                                        minWidth: '50px'
                                                    }}
                                                >
                                                    {getStatusConfig(status).label.replace('고장/수리중', '고장')}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="input-wrapper" style={{ width: '100%', opacity: canEdit ? 1 : 0.7, position: 'relative' }}>
                                        <MessageSquare size={12} className="input-icon" style={{ position: 'absolute', left: '10px', top: '10px', color: '#64748b', zIndex: 1 }} />
                                        <textarea
                                            placeholder="특이사항 기록 (줄바꿈 가능)"
                                            value={item.note}
                                            readOnly={!canEdit}
                                            onChange={(e) => handleNoteChange(item.id, e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '8px 10px 8px 30px',
                                                borderRadius: '8px',
                                                fontSize: '0.8rem',
                                                backgroundColor: 'rgba(0, 0, 0, 0.25)',
                                                border: '1px solid rgba(255, 255, 255, 0.05)',
                                                color: '#f8fafc',
                                                boxSizing: 'border-box',
                                                minHeight: '34px',
                                                maxHeight: '120px',
                                                resize: 'vertical',
                                                fontFamily: 'inherit',
                                                lineHeight: '1.4',
                                                display: 'block'
                                            }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="notes-section" style={{ marginTop: '24px' }}>
                        <label>
                            <MessageSquare size={18} /> 
                            시설 관리 종합 비고 {store?.facilityHistory?.[targetDate] ? <span style={{ color: '#ef4444', marginLeft: '4px', fontWeight: '800' }}>(수정 시 필수입력)</span> : <span style={{ color: '#94a3b8', marginLeft: '4px' }}>(선택사항)</span>}
                        </label>
                        <textarea
                            className="notes-textarea"
                            placeholder="전체적인 시설 상태나 특이사항이 있다면 입력해 주세요."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            disabled={!canEdit}
                        />
                    </div>

                    {canEdit && (
                        <button className="full-width-button" onClick={handleSave} style={{ marginTop: '24px' }}>
                            <Save size={18} style={{ marginRight: '8px' }} /> {currentStoreName || '매장'} 시설 상태 저장
                        </button>
                    )}
                </div>
            </div>
        </Layout >
    );
};

export default Facility;
